import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ParticlesBackground, FloatingOrbs } from './ParticlesBackground';
import { ArrowRight, Sparkles, Play } from 'lucide-react';
import siteConfig, { getPromoText } from '@/config/siteConfig';

export const HeroSection = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-hero-gradient">
      {/* Background Effects */}
      <FloatingOrbs />
      <ParticlesBackground particleCount={40} colors={['gold', 'blue']} />
      
      {/* Stellar/Cosmic overlay */}
      <div className="absolute inset-0 bg-stellar pointer-events-none" />
      <div className="absolute inset-0 bg-cosmic pointer-events-none" />
      <div className="absolute inset-0 bg-stars opacity-30 pointer-events-none" />
      
      {/* Gradient overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/20 to-background pointer-events-none" />
      
      {/* Ocean wave effect at bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-32">
        <svg className="absolute bottom-0 w-full h-32 text-background" viewBox="0 0 1440 120" preserveAspectRatio="none">
          <motion.path
            d="M0,60 C360,120 720,0 1080,60 C1260,90 1380,30 1440,60 L1440,120 L0,120 Z"
            fill="currentColor"
            animate={{
              d: [
                "M0,60 C360,120 720,0 1080,60 C1260,90 1380,30 1440,60 L1440,120 L0,120 Z",
                "M0,80 C360,20 720,100 1080,40 C1260,60 1380,80 1440,40 L1440,120 L0,120 Z",
                "M0,60 C360,120 720,0 1080,60 C1260,90 1380,30 1440,60 L1440,120 L0,120 Z",
              ]
            }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
        </svg>
      </div>

      {/* Content */}
      <div className="container-luxury relative z-10 pt-32 pb-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center lg:text-left"
          >
            {/* Promo Badge - Only show if enabled */}
            {siteConfig.promo.enabled && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="mb-8 inline-block"
              >
                <Badge className="bg-gradient-to-r from-accent to-accent-light text-background border-0 px-5 py-2.5 text-sm font-body font-bold shadow-gold-intense">
                  <Sparkles className="w-4 h-4 mr-2" />
                  JUSQU'À {siteConfig.promo.maxDiscount} • OFFRE LIMITÉE
                </Badge>
              </motion.div>
            )}

            {/* Main Heading */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
            >
              <span className="text-gold-luminous text-lg md:text-xl font-body tracking-[0.3em] uppercase mb-4 block">
                Plonge dans
              </span>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-tight mb-4">
                <span className="text-foreground">L'École des </span>
                <span className="block">Maîtres</span>
                <span className="block text-gradient-gold-shine text-glow-gold">
                  Dauphins
                </span>
              </h1>
            </motion.div>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="text-lg md:text-xl text-muted-foreground font-body leading-relaxed mt-6 mb-8 max-w-xl mx-auto lg:mx-0"
            >
              Un chemin pour vibrer plus fort, aimer ta vie… et, si tu le choisis, 
              transmettre cette fréquence.
            </motion.p>

            {/* Key Points */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="space-y-3 mb-10"
            >
              {[
                'Pour toi qui veux retrouver la joie, la fluidité et le sens.',
                'Pour toi qui veux accompagner avec intégrité et puissance.'
              ].map((point, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-3 text-foreground/90 font-body"
                >
                  <span className="w-2 h-2 rounded-full bg-accent shadow-gold shrink-0" />
                  <span className="text-base md:text-lg">{point}</span>
                </div>
              ))}
            </motion.div>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
            >
              <a href={siteConfig.mainCTALink} target="_blank" rel="noopener noreferrer">
                <Button 
                  size="lg" 
                  className="btn-gold rounded-full px-8 py-6 text-lg group w-full sm:w-auto"
                >
                  Je réserve ma place maintenant
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </a>
              <a href={siteConfig.rdvClarityLink} target="_blank" rel="noopener noreferrer">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="btn-outline-gold rounded-full px-8 py-6 text-lg w-full sm:w-auto"
                >
                  <Play className="w-5 h-5 mr-2" />
                  RDV Clarté
                </Button>
              </a>
            </motion.div>
          </motion.div>

          {/* Hero Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.4 }}
            className="relative"
          >
            <div className="relative aspect-square max-w-lg mx-auto">
              {/* Glow effect behind image */}
              <motion.div
                className="absolute inset-0 rounded-full bg-accent/25 blur-3xl"
                animate={{ 
                  scale: [1, 1.1, 1],
                  opacity: [0.35, 0.55, 0.35]
                }}
                transition={{ duration: 4, repeat: Infinity }}
              />
              
              {/* Main image container */}
              <div className="relative rounded-3xl overflow-hidden border border-accent/30 shadow-gold-intense">
                <img
                  src="https://gillesdelieuze.com/wp-content/uploads/2025/03/gilles1385_dolphins_joy_happiness_neon_stellar_furist_ultra_det_d108e6d0-77b9-454e-9100-27fcbd17df2c-650x650.png"
                  alt="Dauphins cosmiques"
                  className="w-full h-full object-cover"
                />
                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent" />
              </div>

              {/* Floating decorative elements */}
              <motion.div
                className="absolute -top-6 -right-6 w-24 h-24 rounded-2xl glass-gold flex items-center justify-center shadow-gold"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
              >
                <span className="text-3xl font-display text-gradient-gold">14</span>
                <span className="text-xs text-accent ml-1">fréquences</span>
              </motion.div>

              <motion.div
                className="absolute -bottom-4 -left-4 px-6 py-3 rounded-full glass-card shadow-md"
                animate={{ y: [0, 8, 0] }}
                transition={{ duration: 5, repeat: Infinity, delay: 1 }}
              >
                <span className="text-sm text-foreground font-body">
                  ✨ +500 membres transformés
                </span>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
